import { useApolloClient } from "@apollo/client";
import { omit } from "@msys/common";
import {
  LoadingSpinner,
  ModalActions,
  ModalContent,
  ModalDialog,
  ModalTitle,
} from "@msys/ui";
import { SvgIconComponent } from "@mui/icons-material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BusinessIcon from "@mui/icons-material/Business";
import CallIcon from "@mui/icons-material/Call";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EuroIcon from "@mui/icons-material/Euro";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import NotesIcon from "@mui/icons-material/Notes";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  TextField as MuiTextField,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import React from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { AddressInput, PhoneType } from "../../../../clients/graphqlTypes.js";
import { CheckboxField } from "../../../commons/form-fields/CheckboxField.js";
import { EmailField } from "../../../commons/form-fields/EmailField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { assertNever } from "../../../utils.js";
import { AddressField } from "../../addresses/AddressField.js";
import { PhonesField } from "../../phones/PhonesField.js";
import {
  useOnboardingFlowModal_OrganisationDataPrefillLazyQuery,
  useOnboardingFlowModal_UpdateOrganisationMutation,
  useOnboardingFlowModalQuery,
} from "./OnboardingFlowModal.generated.js";
import callAssistantImage from "./OnboardingFlowModal_CallAssistant.png";
import otherImage from "./OnboardingFlowModal_Other.png";

interface OnboardingFormValues {
  website: string | null;
  fullLegalName: string;
  address: AddressInput | null;
  email: string | null;
  phones: { main: boolean; type: PhoneType; number: string }[] | null;
  managingDirector: string | null;
  confirmation: boolean;
}

export type OnboardingFlowStage = "form" | "success";
export const VALID_ONBOARDING_FLOW_STAGES = [
  "form",
  "success",
] satisfies OnboardingFlowStage[];

interface Props {
  stage: OnboardingFlowStage;
  setStage: (newStage: OnboardingFlowStage | null) => void;
}

export const OnboardingFlowModal = ({ stage, setStage }: Props) => {
  const navigate = useNavigate();

  const { t } = useTranslate(["Global", "OrganisationProfile"]);

  const client = useApolloClient();
  const query = useOnboardingFlowModalQuery({ client });
  const viewer = query.data?.viewer;
  const hasCallAssistant =
    viewer?.organisation.capabilities.includes("CALL_ASSISTANT");

  const [updateOrganisation, { loading: updatingOrganisation }] =
    useOnboardingFlowModal_UpdateOrganisationMutation({ client });

  const [website, setWebsite] = React.useState("");
  const [websiteValidationError, setWebsiteValidationError] =
    React.useState<Error | null>(null);
  const [search, organisationDataPrefillQuery] =
    useOnboardingFlowModal_OrganisationDataPrefillLazyQuery();

  return (
    <ModalDialog maxWidth={stage === "success" ? "md" : "sm"}>
      {!viewer ? (
        <LoadingSpinner />
      ) : stage === "form" ? (
        <Formik<OnboardingFormValues>
          initialValues={{
            website: viewer.organisation.website ?? null,
            fullLegalName: viewer.organisation.fullLegalName ?? "",
            address: viewer.organisation.billingAddress
              ? omit(viewer.organisation.billingAddress, "__typename", "id")
              : null,
            email: viewer.organisation.email ?? null,
            phones:
              viewer.organisation.phones.length > 0
                ? viewer.organisation.phones.map(phone =>
                    omit(phone, "__typename", "id")
                  )
                : [
                    {
                      main: true,
                      type: "WORK",
                      number: "",
                    },
                  ],
            managingDirector: viewer.organisation.managingDirector ?? null,
            confirmation: false,
          }}
          validationSchema={Yup.object().shape({
            website: Yup.string().url(),
            fullLegalName: Yup.string().required(),
            address: Yup.object().shape({
              streetLines1: Yup.string().required(),
              postalCode: Yup.string().required(),
              city: Yup.string().required(),
              countryCode: Yup.string().required(),
            }),
            email: Yup.string().email().required(),
            phones: Yup.array().of(
              Yup.object().shape({
                main: Yup.boolean().required(),
                type: Yup.string().required(),
                number: Yup.string().required(),
              })
            ),
            managingDirector: Yup.string().nullable(),
            confirmation: Yup.boolean().oneOf(
              [true],
              t(
                "You need to confirm that the information is correct before proceeding.",
                { ns: "OrganisationProfile" }
              )
            ),
          })}
          onSubmit={async (values, actions) => {
            if (!values.confirmation) {
              return;
            }

            await updateOrganisation({
              variables: {
                input: {
                  website: values.website ?? undefined,
                  fullLegalName: values.fullLegalName,
                  billingAddress: values.address
                    ? {
                        streetLines1: values.address.streetLines1,
                        postalCode: values.address.postalCode,
                        city: values.address.city,
                        countryCode: values.address.countryCode,
                        location: values.address.location
                          ? {
                              lat: values.address.location.lat,
                              lng: values.address.location.lng,
                            }
                          : undefined,
                      }
                    : undefined,
                  phones: values.phones ?? undefined,
                  email: values.email ?? undefined,
                  managingDirector: values.managingDirector ?? undefined,
                },
              },
            });
            setStage("success");
          }}
        >
          {({ values, setFieldValue, dirty, isValid }) => (
            <>
              <ModalTitle
                title={t("Company information", { ns: "OrganisationProfile" })}
              />
              <ModalContent>
                <Form id="onboarding-form">
                  <Stack spacing={2}>
                    <Alert
                      variant="outlined"
                      icon={false}
                      sx={{
                        color: "#96319D",
                        backgroundColor: "#FCFAFC",
                        borderColor: "#FFE2FF",
                        ".MuiAlert-message": {
                          flex: 1,
                        },
                      }}
                      action={
                        <Button
                          variant="outlined"
                          color="inherit"
                          startIcon={<AutoAwesomeIcon />}
                          onClick={async () => {
                            if (!website) return;

                            const { data } = await search({
                              variables: {
                                input: {
                                  organisationName: viewer.organisation.title,
                                  website: website,
                                },
                              },
                            });

                            if (!data) {
                              return;
                            }

                            setFieldValue("website", values.website);
                            setFieldValue(
                              "fullLegalName",
                              data.organisationDataPrefill.fullLegalName ??
                                values.fullLegalName
                            );
                            setFieldValue(
                              "address",
                              data.organisationDataPrefill.address
                            );
                            setFieldValue(
                              "email",
                              data.organisationDataPrefill.email
                            );
                            setFieldValue(
                              "phones",
                              data.organisationDataPrefill.phone
                                ? [
                                    {
                                      main: true,
                                      type: "WORK",
                                      number:
                                        data.organisationDataPrefill.phone,
                                    },
                                  ]
                                : []
                            );
                            setFieldValue(
                              "managingDirector",
                              data.organisationDataPrefill.managingDirector
                            );
                          }}
                          disabled={!website || Boolean(websiteValidationError)}
                          loading={organisationDataPrefillQuery.loading}
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {t("Fill in data", { ns: "OrganisationProfile" })}
                        </Button>
                      }
                    >
                      <AlertTitle>
                        {t("Fill company data with AI", {
                          ns: "OrganisationProfile",
                        })}
                      </AlertTitle>
                      <MuiTextField
                        label="Website"
                        onChange={event => {
                          setWebsite(event.target.value);
                        }}
                        onBlur={async () => {
                          try {
                            await Yup.string()
                              .url()
                              .required()
                              .validate(website);
                            setWebsiteValidationError(null);
                          } catch (error) {
                            setWebsiteValidationError(
                              error instanceof Error
                                ? error
                                : new Error(String(error))
                            );
                          }
                        }}
                        error={Boolean(websiteValidationError)}
                        helperText={
                          websiteValidationError
                            ? websiteValidationError.message
                            : ""
                        }
                        value={website}
                      />
                    </Alert>
                    {organisationDataPrefillQuery.error && (
                      <FormHelperText error>
                        {organisationDataPrefillQuery.error.message}
                      </FormHelperText>
                    )}
                    <Divider />
                    <FormControl
                      component="fieldset"
                      variant="standard"
                      disabled={organisationDataPrefillQuery.loading}
                    >
                      <Stack spacing={1}>
                        <TextField
                          label={t("Full legal name", {
                            ns: "OrganisationProfile",
                          })}
                          name="fullLegalName"
                        />
                        <EmailField
                          label={t("Email", { ns: "OrganisationProfile" })}
                          name="email"
                        />
                        <PhonesField
                          buttonLabel={t("Phone", {
                            ns: "Global",
                          })}
                          name="phones"
                          value={values.phones ?? []}
                        />
                        <AddressField
                          label={t("Address", { ns: "OrganisationProfile" })}
                          name="address"
                        />
                        <TextField
                          label={t("Managing director", {
                            ns: "OrganisationProfile",
                          })}
                          name="managingDirector"
                        />
                        <Box>
                          <CheckboxField
                            label={t(
                              `By clicking "{buttonText}" you are confirming the information`,
                              {
                                ns: "OrganisationProfile",
                                buttonText: t("Continue", { ns: "Global" }),
                              }
                            )}
                            name="confirmation"
                          />
                        </Box>
                      </Stack>
                    </FormControl>
                  </Stack>
                </Form>
              </ModalContent>
              <ModalActions
                actionButtons={[
                  {
                    label: t("Skip", { ns: "Global" }),
                    handleClick: () => {
                      setStage("success");
                    },
                    buttonProps: {
                      color: "primary",
                      variant: "text",
                      endIcon: <KeyboardDoubleArrowRightIcon />,
                      id: "skip-onboarding",
                    },
                  },
                  {
                    label: t("Continue", { ns: "Global" }),
                    buttonProps: {
                      color: "primary",
                      variant: "contained",
                      form: "onboarding-form",
                      type: "submit",
                      endIcon: <ChevronRightIcon />,
                      disabled:
                        organisationDataPrefillQuery.loading ||
                        !dirty ||
                        !isValid,
                    },
                  },
                ]}
              />
            </>
          )}
        </Formik>
      ) : stage === "success" ? (
        <>
          <ModalTitle
            title={t("Welcome to Meister Systems!", {
              ns: "OrganisationProfile",
            })}
          />
          {hasCallAssistant ? (
            <SuccessPageLayout
              imageUrl={callAssistantImage}
              textPrimary={t("Hi, I'm AI-Paul!", {
                ns: "OrganisationProfile",
              })}
              textSecondary={t("Your AI phone assistant.", {
                ns: "OrganisationProfile",
              })}
              steps={[
                {
                  IconComponent: CallIcon,
                  title: t("24/7 Availability", {
                    ns: "OrganisationProfile",
                  }),
                  description: t(
                    "Answers all calls on behalf of your business.",
                    {
                      ns: "OrganisationProfile",
                    }
                  ),
                },
                {
                  IconComponent: NotesIcon,
                  title: t("Qualify inquiries", {
                    ns: "OrganisationProfile",
                  }),
                  description: t(
                    "Asks all important details about the request.",
                    {
                      ns: "OrganisationProfile",
                    }
                  ),
                },
                {
                  IconComponent: EuroIcon,
                  title: t("Prepare quotes", {
                    ns: "OrganisationProfile",
                  }),
                  description: t("Create a quote draft in Meister Systems.", {
                    ns: "OrganisationProfile",
                  }),
                },
              ]}
              actionButtons={[
                {
                  label: t("Later", { ns: "Global" }),
                  handleClick: () => {
                    setStage(null);
                  },
                  buttonProps: {
                    color: "primary",
                    variant: "text",
                  },
                },
                {
                  label: t("Set up now", { ns: "OrganisationProfile" }),
                  handleClick: () => {
                    navigate("/callassistant/onboarding");
                  },
                  buttonProps: {
                    color: "primary",
                    variant: "contained",
                  },
                },
              ]}
            />
          ) : (
            <SuccessPageLayout
              imageUrl={otherImage}
              textPrimary={t("You're all set!", {
                ns: "OrganisationProfile",
              })}
              textSecondary={t(
                "Pro tip: Complete these settings for optimal performance:",
                {
                  ns: "OrganisationProfile",
                }
              )}
              steps={[
                {
                  IconComponent: BusinessIcon,
                  title: t("Company profile", {
                    ns: "OrganisationProfile",
                  }),
                  description: t(
                    "Complete your company details and add the services you offer.",
                    {
                      ns: "OrganisationProfile",
                    }
                  ),
                },
                {
                  IconComponent: PeopleAltIcon,
                  title: t("Invite Team", {
                    ns: "OrganisationProfile",
                  }),
                  description: t(
                    "Send invites to your colleagues to join Meistersystems.",
                    {
                      ns: "OrganisationProfile",
                    }
                  ),
                },
                {
                  IconComponent: DescriptionOutlinedIcon,
                  title: t("Customize Documents", {
                    ns: "OrganisationProfile",
                  }),
                  description: t(
                    "Add prices, configure quote settings and personalize your templates.",
                    {
                      ns: "OrganisationProfile",
                    }
                  ),
                },
              ]}
              actionButtons={[
                {
                  label: t("Go to settings", { ns: "Global" }),
                  handleClick: () => {
                    navigate("/organisation");
                  },
                  buttonProps: {
                    color: "primary",
                    variant: "text",
                  },
                },
                {
                  label: t("Go to dashboard", { ns: "Global" }),
                  handleClick: () => {
                    setStage(null);
                  },
                  buttonProps: {
                    color: "primary",
                    variant: "contained",
                    id: "go-to-dashboard",
                  },
                },
              ]}
            />
          )}
        </>
      ) : (
        assertNever(stage)
      )}
    </ModalDialog>
  );
};

const SuccessPageLayout = ({
  imageUrl,
  textPrimary,
  textSecondary,
  steps,
  actionButtons,
}: {
  imageUrl: string;
  textPrimary: string;
  textSecondary: string;
  steps: {
    IconComponent: SvgIconComponent;
    title: string;
    description: string;
  }[];
  actionButtons: React.ComponentProps<typeof ModalActions>["actionButtons"];
}) => {
  return (
    <>
      <ModalContent>
        <Stack spacing={0} marginBottom={4}>
          <img
            src={imageUrl}
            alt=""
            style={{
              height: "200px",
              objectFit: "cover",
              marginLeft: "-12px",
              marginRight: "-12px",
            }}
          />
          <Stack spacing={3} paddingX={1.5} paddingY={3}>
            <Box>
              <Typography variant="h1">{textPrimary}</Typography>
              <Typography variant="h2" fontWeight="bold">
                {textSecondary}
              </Typography>
            </Box>
            <Grid container columns={{ xs: 1, md: 3 }} spacing={2}>
              {steps.map(({ IconComponent, title, description }, index) => (
                <Grid size={1} key={index}>
                  <Stack spacing={1} direction="row">
                    <IconComponent sx={{ color: "#3867D0" }} />
                    <Stack spacing={1} direction="column">
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        lineHeight={1.5}
                      >
                        {title}
                      </Typography>
                      <Typography variant="body2">{description}</Typography>
                    </Stack>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Stack>
      </ModalContent>
      <ModalActions actionButtons={actionButtons} />
    </>
  );
};
